import { shallowRef } from 'vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { SELF_PACED_COURSE_KEY } from '@/constants/CourseTypes.js';

const CONTENT_EDITOR_ROLE_NAME = 'Content Editor';

/** Matches RestrictCourseAccessPolicy::enrollmentHasInstructorAccess. */
const ENROLLED_INSTRUCTOR_ROLE_NAMES = ['Instructor', 'Instruction Admin', 'Instructional Assistant'];

const passedPreviewCutoffs = shallowRef(new Set());
const pendingPreviewCutoffTimers = new Map();

function schedulePreviewCutoff(previewMs) {
    if (pendingPreviewCutoffTimers.has(previewMs)) {
        return;
    }

    const timeout = setTimeout(
        () => {
            pendingPreviewCutoffTimers.delete(previewMs);

            if (Date.now() < previewMs) {
                schedulePreviewCutoff(previewMs);
                return;
            }

            passedPreviewCutoffs.value = new Set(passedPreviewCutoffs.value).add(previewMs);
        },
        Math.min(2_147_483_647, Math.max(0, previewMs - Date.now()))
    );

    pendingPreviewCutoffTimers.set(previewMs, timeout);
}

function isSelfPacedCourseRecord(course) {
    if (course?.course_type?.title === SELF_PACED_COURSE_KEY) {
        return true;
    }

    const types = typeof window !== 'undefined' ? window.__INITIAL_STATE__?.CourseTypes : null;
    const match = Array.isArray(types) ? types.find(type => type.id === course?.course_type_id) : null;

    return match?.title === SELF_PACED_COURSE_KEY;
}

function previewBufferDays({ previewBuffer, store } = {}) {
    if (previewBuffer != null && previewBuffer !== '') {
        return Number(previewBuffer) || 0;
    }

    const fromStore = store?.getters?.['InstitutionSettings/get']?.('course_preview_buffer');

    if (fromStore != null && fromStore !== '') {
        return Number(fromStore) || 0;
    }

    const fromWindow =
        typeof window !== 'undefined' ? window.__INITIAL_STATE__?.InstitutionSettings?.course_preview_buffer : null;

    if (fromWindow != null && fromWindow !== '') {
        return Number(fromWindow) || 0;
    }

    return 0;
}

/**
 * Mirrors RestrictCourseAccessPolicy::userIsOnlyContentEditorOfCourseGroup. The Content
 * Editor lock applies only to users whose sole role in the course's groups is Content
 * Editor; a user who also holds another group role (for example Instructor) gets the access
 * that role grants, so callers must not route them through the lock.
 *
 * @param {object|null|undefined} user
 * @param {object|number|string|null|undefined} course
 * @returns {boolean}
 */
export function userIsOnlyContentEditorOfCourseGroup(user, course) {
    if (user == null || course == null) {
        return false;
    }

    return Boolean(user.hasOnlyRoleWithinCourseGroup?.(course, CONTENT_EDITOR_ROLE_NAME));
}

/**
 * Mirrors RestrictCourseAccessPolicy::userHasEnrolledInstructorAccess.
 *
 * @param {object|null|undefined} user
 * @param {object|number|string|null|undefined} course
 * @returns {boolean}
 */
export function userHasEnrolledInstructorAccess(user, course) {
    const courseId = typeof course === 'object' ? course?.id : course;

    if (user == null || courseId == null || courseId === '') {
        return false;
    }

    const enrollment =
        typeof user.enrollmentFor === 'function'
            ? user.enrollmentFor(courseId)
            : (user.enrollments ?? []).find(item => item.course_id === Number.parseInt(courseId, 10));

    if (!enrollment) {
        return false;
    }

    return (enrollment.roles ?? []).some(role => ENROLLED_INSTRUCTOR_ROLE_NAMES.includes(role.name));
}

/**
 * Group Content Editors may author a dated time-based course until learners
 * gain access (start minus the institution preview buffer). Self-paced and
 * undated courses stay authorable. Corporate is not date-gated. Enrolled
 * instructors keep access even when they also hold the group Content Editor role.
 * Only call this for users for whom userIsOnlyContentEditorOfCourseGroup() is true.
 *
 * @param {object|null|undefined} course
 * @param {{ isCorporate?: boolean, previewBuffer?: number|string, store?: object, user?: object }} [options]
 * @returns {boolean}
 */
export function groupContentEditorMayEditCourse(course, options = {}) {
    if (options.isCorporate ?? isCorporateClient()) {
        return true;
    }

    if (!course) {
        return false;
    }

    if (userHasEnrolledInstructorAccess(options.user, course)) {
        return true;
    }

    if (isSelfPacedCourseRecord(course)) {
        return true;
    }

    const start = course.course_settings?.start_date;
    const end = course.course_settings?.end_date;

    if (!start || !end) {
        return true;
    }

    const opensAt = Date.parse(start);

    if (Number.isNaN(opensAt)) {
        return true;
    }

    const previewMs = opensAt - previewBufferDays(options) * 24 * 60 * 60 * 1000;
    const passedCutoffs = passedPreviewCutoffs.value;
    const isBeforePreviewCutoff = Date.now() < previewMs;

    if (isBeforePreviewCutoff && !passedCutoffs.has(previewMs)) {
        schedulePreviewCutoff(previewMs);
        return true;
    }

    return false;
}
